import React, { createContext, useContext } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getProductData, getProductName } from "../services/Product/ProductService/productService";
import { fetchResource, queryKeys } from "../services/ReactQuery/reactQueryService";
import { ProductUnion } from "../types/ProductUnion";

type CaseProviderData = {
  product: ProductUnion | null;
  isLoading: boolean;
  error: Error | null;
  refreshCase: () => Promise<void>;
};

const CaseProviderContext = createContext<CaseProviderData | null>(null);

export default function CaseProvider({
  children,
  caseUrlPart,
  caseId,
}: {
  children?: React.ReactNode;
  caseUrlPart: string;
  caseId: number;
}) {
  const queryClient = useQueryClient();
  const caseUrl = getProductData(getProductName(caseUrlPart), "apiUri");

  const {
    data: caseEntity,
    error,
    isLoading,
  } = useQuery({
    queryKey: queryKeys.item(caseUrl, caseId),
    queryFn: () => fetchResource(caseUrl, caseId),
    retry: false,
  });

  const refreshCase = async () => {
    return queryClient.invalidateQueries({
      queryKey: queryKeys.item(caseUrl, caseId),
    });
  };

  const data: CaseProviderData = {
    product: caseEntity ?? null,
    isLoading,
    error,
    refreshCase,
  };

  return <CaseProviderContext.Provider value={data}>{children}</CaseProviderContext.Provider>;
}

export const useCase = () => {
  const caseContext = useContext(CaseProviderContext);

  if (!caseContext) {
    throw new Error("useCase can only be used inside CaseProvider");
  }

  return caseContext;
};
